/**
 * Temporary screen-scoped design tokens for **Inbox** (`/requests/inbox`).
 *
 * Mirrors `Inbox Requests.dc.html` 1:1 — every colour, size, radius, spacing
 * and type value the screen needs lives here so the components never inline a
 * literal. Deliberately kept separate from `SignRequests.tokens.ts` even where
 * values coincide: the two screens are re-skinned independently and the design
 * gives Inbox its own header button metrics and row layout (see
 * UI_IMPLEMENTATION_RULES.md § Temporary Screen Tokens / Do Not Force
 * Consistency).
 */

import { button, contentCard, formField, inkfree, notification, signer, white } from "theme/colors";

export const inboxTokens = {
  /**
   * Inner padding only — the content column itself comes from the shared
   * `CenteredAuthenticatedLayout`, so Inbox lines up with Sent and Templates.
   */
  page: {
    paddingTop: "32px",
    paddingX: "40px",
    paddingBottom: "48px",
  },

  /** "Inbox" title block + the single header action. */
  header: {
    marginBottom: "24px",
    gap: "12px",
    titleColor: signer.ink,
    titleFontSize: "26px",
    titleFontWeight: 800,
    titleLetterSpacing: "-.02em",
    subtitleColor: signer.text.subtitle,
    subtitleFontSize: "13.5px",
    subtitleMarginTop: "5px",
  },

  /** Filled "Create Request" button — wider and a notch larger than Sent's. */
  primaryButton: {
    bg: inkfree.main,
    hoverBg: inkfree.dark,
    color: white,
    radius: "10px",
    padding: "13px 20px",
    fontSize: "14px",
    fontWeight: 700,
    gap: "9px",
    iconSize: 16,
    /**
     * Caret half of the split button. The header design draws a single button;
     * the app needs the request-type menu, so the caret reuses the width and
     * darker fill the sidebar and Sent header give theirs.
     */
    caretWidth: 42,
    caretBg: inkfree.dark,
    caretHoverBg: inkfree.darkest,
    caretDivider: "rgba(255,255,255,.25)",
  },

  /** Search field + date-range trigger. */
  toolbar: {
    gap: "14px",
    marginBottom: "18px",
    bg: white,
    border: formField.borderWarm,
    radius: "11px",
    padding: "12px 16px",
    focusBorder: signer.orange.main,
    hoverBorder: "#D8D2C2",
    searchGap: "11px",
    searchIconSize: 18,
    searchIconColor: signer.text.faint,
    inputFontSize: "14px",
    inputFontWeight: 400,
    /**
     * The design's search field is 46px tall and its text starts 2px in from
     * the input box: its bare `<input>` keeps the `1px 2px` padding a browser
     * gives text inputs. MUI zeroes that, so it is restored here rather than
     * faking the height and inset on the wrapper.
     */
    inputPadding: "1px 2px",
    inputColor: signer.ink,
    placeholderColor: signer.text.faint,
    searchPlaceholder: "Search by document, sender, or status",
    dateGap: "9px",
    dateFontSize: "13.5px",
    dateFontWeight: 600,
    dateColor: signer.text.meta,
    dateIconSize: 16,
    dateCaretSize: 13,
  },

  /** Signer-perspective pill tabs with their counts. */
  tabs: {
    gap: "8px",
    marginBottom: "16px",
    padding: "9px 15px",
    radius: "999px",
    fontSize: "13px",
    fontWeight: 700,
    labelGap: "8px",
    activeBg: signer.ink,
    activeBorder: signer.ink,
    activeColor: white,
    bg: white,
    border: formField.borderWarm,
    color: signer.text.meta,
    countMinWidth: 20,
    countPadding: "1px 6px",
    countRadius: "999px",
    countFontSize: "11px",
    countFontWeight: 700,
    countActiveBg: "rgba(255,255,255,.2)",
    countActiveColor: white,
    countBg: signer.border.divider,
    countColor: signer.text.subtitle,
  },

  /** White card wrapping every request row. */
  list: {
    bg: contentCard.bg,
    border: contentCard.border,
    radius: "14px",
    shadow: contentCard.shadow,
  },

  /** One request row: accent | tile | text | status chip | action slot. */
  row: {
    gap: "16px",
    padding: "16px 24px",
    divider: signer.border.divider,
    hoverBg: signer.surface.hover,
    /** Left rail that flags the rows still waiting on the reader. */
    accentWidth: 3,
    iconTileSize: 40,
    iconTileRadius: "9px",
    iconSize: 18,
    titleFontSize: "14px",
    titleFontWeight: 700,
    titleColor: signer.ink,
    /** Closed rows (expired / declined / void) dim their title. */
    titleMutedColor: "#6F6957", // WCAG 1.4.3: #8A836F was 3.77:1 on white
    subFontSize: "12.5px",
    subColor: signer.text.faint,
    subMarginTop: "3px",
    /**
     * Fixed slot so every status chip ends on the same vertical line whether
     * the row carries a button or not.
     */
    actionSlotWidth: 118,
  },

  /** Dot + pill that labels each row's state. */
  status: {
    chipGap: "7px",
    chipPadding: "5px 11px",
    chipRadius: "999px",
    dotSize: 7,
    labelFontSize: "12px",
    labelFontWeight: 700,
  },

  /** "Sign" (filled) and "Download" (outlined) row buttons. */
  rowAction: {
    radius: "9px",
    fontSize: "13px",
    fontWeight: 700,
    primaryPadding: "9px 18px",
    primaryBg: inkfree.main,
    primaryHoverBg: inkfree.dark,
    primaryColor: white,
    secondaryPadding: "9px 15px",
    secondaryBg: white,
    secondaryBorder: button.secondaryBorderWarm,
    secondaryColor: signer.text.secondary,
    secondaryHoverBorder: signer.ink,
    secondaryHoverColor: signer.ink,
  },

  /** "Showing 1–5 of 13 requests" + the numbered pager. */
  footer: {
    marginTop: "18px",
    labelFontSize: "12.5px",
    labelColor: signer.text.faint,
    gap: "6px",
    pageMinWidth: 32,
    pageHeight: 32,
    pagePadding: "0 6px",
    pageRadius: "8px",
    pageFontSize: "13px",
    pageFontWeight: 700,
    pageActiveBg: inkfree.main,
    pageActiveBorder: inkfree.main,
    pageActiveColor: white,
    pageBg: white,
    pageBorder: formField.borderWarm,
    pageColor: signer.text.meta,
    arrowSize: 32,
    arrowIconSize: 15,
    arrowDisabledColor: "#CFCADD",
    /**
     * Long lists collapse to `1 … 18 19 20 … 35` — a constant seven page slots
     * either side of the arrows, whatever the total.
     */
    boundaryCount: 1,
    siblingCount: 1,
    ellipsisColor: signer.text.faint,
    /** Jumping beats clicking once the window starts hiding pages. */
    jumpMinPages: 6,
    jumpGap: "10px",
    jumpLabelFontSize: "12.5px",
    jumpLabelColor: signer.text.faint,
    jumpInputWidth: 46,
    jumpInputPaddingX: "6px",
    jumpInputBg: white,
    jumpInputBorder: formField.borderWarm,
    jumpInputFocusBorder: signer.orange.main,
    jumpInputColor: signer.ink,
    jumpInputPlaceholderColor: signer.text.faint,
    jumpInputFontSize: "13px",
    jumpInputFontWeight: 700,
  },

  /** Full-card placeholder for an inbox that has never received anything. */
  freshState: {
    border: contentCard.border,
    radius: "16px",
    shadow: "0 1px 3px rgba(33,21,71,.05)",
    padding: "110px 24px",
    iconSize: 72,
    iconBg: signer.orange.tint,
    iconStroke: signer.orange.main,
    glyphSize: 30,
    iconMarginBottom: "20px",
    titleFontSize: "18px",
    titleFontWeight: 800,
    titleColor: signer.ink,
    bodyFontSize: "13.5px",
    bodyColor: signer.text.subtitle,
    bodyMaxWidth: 430,
    bodyLineHeight: 1.65,
    bodyMarginTop: "8px",
    hintMarginTop: "22px",
    hintFontSize: "12px",
    hintColor: button.disabledColor,
    hintStrongColor: signer.text.subtitle,
    hintStrongWeight: 700,
  },

  /** In-place card shown when a search or filter matches nothing. */
  noResults: {
    border: contentCard.border,
    radius: "14px",
    shadow: "0 1px 3px rgba(33,21,71,.05)",
    padding: "80px 24px",
    iconSize: 64,
    iconBg: signer.surface.muted,
    iconStroke: signer.text.faint,
    glyphSize: 26,
    iconMarginBottom: "18px",
    titleFontSize: "17px",
    titleFontWeight: 800,
    titleColor: signer.ink,
    bodyFontSize: "13.5px",
    bodyColor: signer.text.subtitle,
    bodyMaxWidth: 400,
    bodyLineHeight: 1.6,
    bodyMarginTop: "7px",
    buttonMarginTop: "20px",
    buttonHeight: 42,
    buttonPadding: "0 20px",
    buttonRadius: "10px",
    buttonBorder: button.secondaryBorderWarm,
    buttonBg: white,
    buttonColor: signer.text.secondary,
    buttonFontSize: "13px",
    buttonFontWeight: 700,
    buttonHoverBorder: signer.ink,
    buttonHoverColor: signer.ink,
  },
} as const;

/**
 * Raw media query for the narrow phone column of
 * `Inbox Requests - Mobile & Tablet.dc.html`.
 *
 * The reference draws 375 and 390 differently — the narrow column shortens the
 * search placeholder to "Search inbox" and carries no Create control at all,
 * while the standard column keeps the full placeholder and parks a square
 * Create button beside the strapline. MUI has no breakpoint between the two, so
 * the split is a raw query applied *after* the `xs` values so it wins.
 */
export const PHONE_SM_QUERY = "@media (max-width:389.98px)";

/**
 * Phone (`xs`) and tablet (`sm`) values, traced from
 * `Inbox Requests - Mobile & Tablet.dc.html`.
 *
 * Only the values that actually differ from the desktop tokens above live here;
 * anything omitted is inherited, so the desktop screen is untouched. `sm` /
 * `std` inside `phone` are the 375 and 390 columns of the reference.
 */
export const inboxResponsiveTokens = {
  phone: {
    /** Content column: 14px gutters at 375, 16px at 390. */
    paddingX: { sm: "14px", std: "16px" },
    paddingTop: { sm: "16px", std: "18px" },
    paddingBottom: { sm: "32px", std: "34px" },
    /** One column gap replaces the desktop screen's per-block bottom margins. */
    gap: { sm: "14px", std: "15px" },

    /** The `<h1>` moves into the app bar; only the strapline stays on the page. */
    subtitleFontSize: { sm: "12.5px", std: "13px" },
    subtitleLineHeight: 1.45,

    /**
     * Square Create button drawn beside the strapline from 390 up. The 375
     * column of the reference carries no Create control at all.
     */
    /**
     * Full-width "Create Request" under the header. Same metrics as Sent
     * requests, Templates and Web Forms so the four read as one control.
     */
    createButton: {
      padding: "13px 14px",
      radius: "11px",
      fontSize: "14px",
      gap: "9px",
      iconSize: 16,
    },

    /** Search only — the date-range chip is dropped on a phone. */
    search: {
      radius: "11px",
      padding: "11px 13px",
      gap: "9px",
      iconSize: 16,
      inputFontSize: "13px",
      /** 375 has no room for the full sentence. */
      placeholderShort: "Search inbox",
    },

    /** Chips run as one horizontally-scrolling line instead of wrapping. */
    tabs: {
      gap: "8px",
      padding: { sm: "8px 13px", std: "9px 14px" },
      fontSize: "12.5px",
      labelGap: "7px",
      countMinWidth: 19,
      countFontSize: "10.5px",
    },

    /** Every row becomes its own card, stacked with a gap. */
    list: { gap: "10px" },

    row: {
      radius: "14px",
      padding: { sm: "13px 14px 13px 17px", std: "14px 16px 14px 19px" },
      /** Between the document block and the status / action line. */
      gap: { sm: "10px", std: "11px" },
      /** Between the icon tile and the title block. */
      headGap: { sm: "11px", std: "12px" },
      /** The rail is inset from the card's rounded corners and rounded itself. */
      accentInset: { sm: "11px", std: "12px" },
      accentRadius: "2px",
      iconTileSize: { sm: 36, std: 38 },
      iconTileRadius: "10px",
      iconSize: { sm: 16, std: 17 },
      titleFontSize: { sm: "12.5px", std: "13px" },
      subFontSize: { sm: "11px", std: "11.5px" },
      /** Status chip to action button on the second line. */
      metaGap: "10px",
    },

    status: {
      chipPadding: { sm: "4px 10px", std: "5px 10px" },
      chipGap: "6px",
      dotSize: 6,
      labelFontSize: { sm: "10.5px", std: "11px" },
    },

    rowAction: {
      radius: "9px",
      fontSize: "12.5px",
      primaryPadding: "8px 15px",
      secondaryPadding: "8px 13px",
    },

    /** "1–5 of 11" — the phone drops "Showing" and the noun. */
    footer: {
      paddingTop: "2px",
      labelFontSize: { sm: "11.5px", std: "12px" },
    },

    freshState: {
      radius: "16px",
      padding: { sm: "44px 20px", std: "52px 22px" },
      iconSize: { sm: 62, std: 66 },
      glyphSize: { sm: 26, std: 28 },
      iconMarginBottom: { sm: "16px", std: "17px" },
      titleFontSize: { sm: "16px", std: "17px" },
      bodyFontSize: { sm: "12.5px", std: "13px" },
      bodyLineHeight: 1.6,
      hintFontSize: { sm: "11.5px", std: "12px" },
      hintMarginTop: "18px",
    },

    noResults: {
      radius: "14px",
      padding: { sm: "44px 18px", std: "48px 20px" },
      iconSize: { sm: 52, std: 56 },
      glyphSize: { sm: 22, std: 23 },
      iconMarginBottom: { sm: "14px", std: "15px" },
      titleFontSize: { sm: "14.5px", std: "15px" },
      bodyFontSize: { sm: "12px", std: "12.5px" },
      bodyLineHeight: 1.55,
      bodyMarginTop: "6px",
      buttonMarginTop: { sm: "16px", std: "18px" },
      buttonHeight: { sm: 40, std: 42 },
      buttonPadding: { sm: "0 16px", std: "0 18px" },
      buttonRadius: { sm: "9px", std: "10px" },
      buttonFontSize: { sm: "12.5px", std: "13px" },
    },
  },

  tablet: {
    paddingX: "26px",
    paddingTop: "26px",
    paddingBottom: "40px",
    gap: "18px",

    titleFontSize: "24px",
    subtitleFontSize: "13px",
    headerGap: "20px",

    toolbar: {
      gap: "12px",
      padding: "12px 15px",
      searchGap: "11px",
      searchIconSize: 17,
      inputFontSize: "13.5px",
      dateFontSize: "13px",
      dateGap: "9px",
      dateIconSize: 16,
    },

    row: {
      padding: "15px 20px",
      gap: "14px",
      iconTileSize: 38,
      iconTileRadius: "9px",
      iconSize: 17,
      titleFontSize: "13.5px",
      subFontSize: "12px",
      actionSlotWidth: 110,
    },

    rowAction: {
      fontSize: "12.5px",
      primaryPadding: "8px 15px",
      secondaryPadding: "8px 13px",
    },

    freshState: {
      padding: "96px 24px",
      bodyMaxWidth: 440,
    },

    noResults: {
      padding: "70px 24px",
      iconSize: 60,
      glyphSize: 24,
      iconMarginBottom: "16px",
      titleFontSize: "16px",
      bodyFontSize: "13px",
      bodyMarginTop: "6px",
      buttonMarginTop: "18px",
      buttonPadding: "0 20px",
    },
  },
} as const;

/**
 * Per-state palette written from the **reader's** point of view: the same
 * `Pending` request is "Your turn to sign" for the current signer and "Waiting
 * for others" for everyone else. `accent` paints the row's left rail and is
 * transparent for every state but the one needing action.
 */
export const inboxStatusTokens = {
  action: {
    label: "Your turn to sign",
    bg: signer.orange.tint,
    dot: signer.orange.main,
    color: "#B84208",
    accent: signer.orange.main,
    iconBg: signer.orange.tint,
    iconStroke: signer.orange.main,
    muted: false,
  },
  waiting: {
    label: "Waiting for others",
    bg: "#EAF2FC",
    dot: "#2A7DE1",
    color: "#1B5DAC",
    accent: "transparent",
    iconBg: signer.border.divider,
    iconStroke: signer.purple.main,
    muted: false,
  },
  completed: {
    label: "Signed",
    bg: notification.success.bg,
    dot: notification.success.icon,
    color: notification.success.title,
    accent: "transparent",
    iconBg: notification.success.bg,
    iconStroke: notification.success.icon,
    muted: false,
  },
  expired: {
    label: "Expired",
    bg: "#F3F1EC",
    dot: "#A69F8C",
    color: "#6F6957", // WCAG 1.4.3: #8A836F was 3.34:1 on its chip fill
    accent: "transparent",
    iconBg: "#F3F1EC",
    iconStroke: "#A69F8C",
    muted: true,
  },
  declined: {
    label: "Declined",
    bg: notification.error.bg,
    dot: notification.error.icon,
    color: notification.error.title,
    accent: "transparent",
    iconBg: notification.error.bg,
    iconStroke: notification.error.icon,
    muted: true,
  },
  void: {
    label: "Void",
    bg: signer.border.light,
    dot: signer.text.subtitle,
    color: signer.text.meta,
    accent: "transparent",
    iconBg: signer.border.light,
    iconStroke: signer.text.subtitle,
    muted: true,
  },
} as const;

export type InboxStatusKey = keyof typeof inboxStatusTokens;

export default inboxTokens;
